import { useEffect, useState } from "react";
import { useParams } from "wouter";
import {
  Shield,
  ShieldX,
  FileText,
  Car,
  User,
  Building2,
  Calendar,
  Loader2,
} from "lucide-react";
import logoImg from "@/assets/images/logo.png";

interface VerificarLaudoData {
  laudoId: string;
  cartela: string;
  status: string;
  autenticado: boolean;
  dataAssinatura: string | null;
  criadoEm: string;
  veiculo: {
    placa: string;
    marca: string;
    modelo: string;
    ano_fabricacao: number | null;
    chassi: string;
  } | null;
  responsavelTecnico: {
    nome: string;
    crea: string | null;
  } | null;
  vistoriadora: {
    nome: string;
  } | null;
}

const STATUS_LABEL: Record<string, string> = {
  rascunho: "Rascunho",
  enviado_analise: "Enviado para Análise",
  em_vistoria: "Em Vistoria",
  em_avaliacao_rt: "Em Avaliação RT",
  assinado: "Assinado",
  finalizado: "Finalizado",
};

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function VerificarLaudo() {
  const params = useParams<{ laudoId: string }>();
  const laudoId = params.laudoId;

  const [data, setData] = useState<VerificarLaudoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);

  const _tirarCrea = (v: string) => {
    return v.replaceAll("CREA", "").replaceAll("-", "");
  };

  useEffect(() => {
    if (!laudoId) return;
    fetch(`/api/verificar/${laudoId}`)
      .then(async (res) => {
        if (res.status === 404) {
          setNotFound(true);
          return;
        }
        if (!res.ok) {
          setError(true);
          return;
        }
        setData(await res.json());
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [laudoId]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-start py-10 px-4">
      <div className="w-full max-w-xl">
        {/* Header */}
        <div className="flex flex-col items-center mb-8">
          <img
            src={logoImg}
            alt="LaudoLegal"
            className="h-10 mb-3 object-contain"
          />
          <p className="text-slate-500 text-sm">
            Verificação de Autenticidade de Laudo
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        )}

        {/* Erro de servidor */}
        {!loading && error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <ShieldX className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <p className="text-red-700 font-medium">
              Erro ao consultar o laudo.
            </p>
            <p className="text-red-500 text-sm mt-1">
              Tente novamente em alguns instantes.
            </p>
          </div>
        )}

        {/* Não encontrado */}
        {!loading && notFound && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 text-center">
            <ShieldX className="w-10 h-10 text-orange-400 mx-auto mb-3" />
            <p className="text-orange-700 font-medium">Laudo não encontrado.</p>
            <p className="text-orange-500 text-sm mt-1">
              O código{" "}
              <span className="font-mono font-semibold">{laudoId}</span> não
              corresponde a nenhum laudo registrado no sistema.
            </p>
          </div>
        )}

        {/* Resultado */}
        {!loading && data && (
          <div className="space-y-4">
            {/* Selo de autenticidade */}
            <div
              className={`rounded-xl p-5 flex items-center gap-4 border ${
                data.autenticado
                  ? "bg-green-50 border-green-200"
                  : "bg-yellow-50 border-yellow-200"
              }`}
            >
              {data.autenticado ? (
                <Shield className="w-10 h-10 text-green-500 shrink-0" />
              ) : (
                <ShieldX className="w-10 h-10 text-yellow-500 shrink-0" />
              )}
              <div>
                <p
                  className={`font-semibold text-base ${
                    data.autenticado ? "text-green-800" : "text-yellow-800"
                  }`}
                >
                  {data.autenticado
                    ? "Documento Autêntico"
                    : "Documento em Processamento"}
                </p>
                <p
                  className={`text-sm ${
                    data.autenticado ? "text-green-600" : "text-yellow-600"
                  }`}
                >
                  {data.autenticado
                    ? "Este laudo foi assinado digitalmente por um Responsável Técnico habilitado."
                    : `Status atual: ${STATUS_LABEL[data.status] ?? data.status}`}
                </p>
              </div>
            </div>

            {/* Identificação do laudo */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
                <FileText className="w-4 h-4 text-slate-400" />
                <span className="text-slate-500 text-sm font-medium uppercase tracking-wide">
                  Identificação do Laudo
                </span>
              </div>
              <Row label="Código do Laudo" value={data.laudoId} mono />
              <Row label="Cartela" value={data.cartela ?? "—"} mono />
              <Row
                label="Status"
                value={STATUS_LABEL[data.status] ?? data.status}
              />
              {data.dataAssinatura && (
                <Row
                  label="Assinado em"
                  value={formatDate(data.dataAssinatura)}
                />
              )}
              <Row label="Criado em" value={formatDate(data.criadoEm)} />
            </div>

            {/* Veículo */}
            {data.veiculo && (
              <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
                  <Car className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-500 text-sm font-medium uppercase tracking-wide">
                    Veículo
                  </span>
                </div>
                <Row label="Placa" value={data.veiculo.placa} mono />
                <Row
                  label="Marca / Modelo"
                  value={`${data.veiculo.marca} ${data.veiculo.modelo}`}
                />
                {data.veiculo.ano_fabricacao && (
                  <Row
                    label="Ano de Fabricação"
                    value={String(data.veiculo.ano_fabricacao)}
                  />
                )}
                <Row label="Chassi" value={data.veiculo.chassi} mono />
              </div>
            )}

            {/* RT e Empresa */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
              {data.responsavelTecnico && (
                <>
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500 text-sm font-medium uppercase tracking-wide">
                      Responsável Técnico
                    </span>
                  </div>
                  <Row label="Nome" value={data.responsavelTecnico.nome} />
                  {data.responsavelTecnico.crea && (
                    <Row
                      label="CREA"
                      value={_tirarCrea(data.responsavelTecnico.crea)}
                    />
                  )}
                </>
              )}
              {data.vistoriadora && (
                <>
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-3 mt-4">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500 text-sm font-medium uppercase tracking-wide">
                      Empresa Vistoriadora
                    </span>
                  </div>
                  <Row label="Razão Social" value={data.vistoriadora.nome} />
                </>
              )}
            </div>

            <p className="text-center text-xs text-slate-400 pb-4">
              Consulta realizada em {formatDate(new Date().toISOString())} ·
              LaudoLegal
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex justify-between items-start gap-4">
      <span className="text-slate-500 text-sm shrink-0">{label}</span>
      <span
        className={`text-slate-800 text-sm text-right ${mono ? "font-mono" : "font-medium"}`}
      >
        {value}
      </span>
    </div>
  );
}
